/**
 * utils/pvSuccessProcessor.js
 * Processeur principal pour traiter les données des PV de succès
 */

import { SorProcessorUtils } from "./sorProcessorUtils.js";
import { DirectoryScanner } from "./directoryScanner.js";
import { SorFileManager } from "./sorFileManager.js";
import { SorMetadataExtractor } from "./sorMetadataExtractor.js";

export class PVSuccessProcessor {
  /**
   * Traiter un répertoire sélectionné pour les PV de succès
   * @param {FileSystemDirectoryHandle} directoryHandle - Handle du répertoire
   * @param {Object} [options] - Options de traitement
   * @param {boolean} [options.allowEmpty=false] - Si true, accepte les dossiers vides
   */
  static async processDirectory(directoryHandle, options = {}) {
    const { allowEmpty = false } = options;

    try {
      // Scanner le répertoire avec l'option allowEmpty
      const scanResult = await DirectoryScanner.scanDirectory(directoryHandle, { allowEmpty });

      if (!scanResult.isValid) {
        return scanResult;
      }

      // Si le dossier est vide et autorisé, retourner un résultat vide valide
      if (scanResult.data.files.length === 0 && allowEmpty) {
        return {
          isValid: true,
          data: { fibers: [] },
          errors: [],
        };
      }

      // Enrichir tous les fichiers avec métadonnées des fichier sor
      for (const file of scanResult.data.files) {
        await SorMetadataExtractor.enrichFileObject(file);
      }
      // Grouper les fichiers
      const grouped = SorFileManager.groupByLocation(scanResult.data.files);
      // Valider chaque fibre
      this._validateFibers(grouped);

      // Convertir en array simple pour l'app state
      const fibers = this._convertToArray(grouped);
      return {
        isValid: true,
        data: { fibers },
        errors: [],
      };
    } catch (error) {
      return {
        isValid: false,
        errors: [`Erreur: ${error.message}`],
        data: null,
      };
    }
  }

  /**
   * Convertir la structure groupée en array simple
   */
  static _convertToArray(grouped) {
    const fibers = [];

    Object.keys(grouped).forEach((tiroir) => {
      Object.keys(grouped[tiroir]).forEach((module) => {
        Object.values(grouped[tiroir][module]).forEach((fiber, index) => {
          try {
            // Vérifier la structure de base
            SorProcessorUtils.validateFiberStructure(fiber, ["folderPBOs", "pboValues", "mesures"]);

            const moduleCode = SorProcessorUtils.extractModuleCode(module);
            const REFERENCE = SorProcessorUtils.buildReference(tiroir, moduleCode);
            const pboFolder = SorProcessorUtils.getFirstFromSet(fiber.folderPBOs);
            const bpoFile = SorProcessorUtils.getFirstFromSet(fiber.pboValues);

            fibers.push({
              REFERENCE,
              tiroir,
              module: moduleCode,
              port: fiber.port,
              pboFolder,
              bpoFile,
              nm1310: !!fiber.mesures[1310],
              nm1550: !!fiber.mesures[1550],
              files: fiber.files,
            });
          } catch (error) {
            SorProcessorUtils.logFiberError(error, "pvSuccess", index, tiroir, module, {
              port: fiber?.port,
              folderPBOs: fiber?.folderPBOs ? Array.from(fiber.folderPBOs) : [],
              pboValues: fiber?.pboValues ? Array.from(fiber.pboValues) : [],
              mesures: fiber?.mesures ? Object.keys(fiber.mesures) : [],
              files: fiber?.files?.length || 0,
            });
            throw error;
          }
        });
      });
    });

    return SorProcessorUtils.sortFibers(fibers);
  }

  /**
   * Valider toutes les fibres avec détection des erreurs avancées
   */
  static _validateFibers(grouped) {
    Object.values(grouped).forEach((modules) => {
      Object.values(modules).forEach((fibres) => {
        Object.values(fibres).forEach((fiber) => {
          // Validation PBO : cohérence entre dossier et fichiers
          fiber.pboValidation = this._validateFiberPBO(fiber);

          // Validation mesures : nombre et longueurs d'onde
          fiber.mesureValidation = this._validateFiberMeasures(fiber);

          // Validation doublons : fichiers en double
          fiber.duplicateValidation = this._validateFiberDuplicates(fiber);

          // Status global de la fibre
          fiber.isValid =
            fiber.pboValidation.isValid && fiber.mesureValidation.isValid && fiber.duplicateValidation.isValid;
        });
      });
    });
  }

  /**
   * Valider le PBO d'une fibre (dossier vs fichier)
   */
  static _validateFiberPBO(fiber) {
    return SorFileManager.validatePBO(fiber.pboValues, fiber.folderPBOs);
  }

  /**
   * Valider les mesures d'une fibre
   */
  static _validateFiberMeasures(fiber) {
    const has1310 = !!fiber.mesures[1310];
    const has1550 = !!fiber.mesures[1550];

    // Compter le nombre total de fichiers
    const totalFiles = fiber.files.length;

    // Compter les fichiers par longueur d'onde
    const count1310 = fiber.files.filter((f) => f.parsed?.longueurOnde === 1310).length;
    const count1550 = fiber.files.filter((f) => f.parsed?.longueurOnde === 1550).length;
    const countOther = fiber.files.filter(
      (f) => f.parsed?.longueurOnde !== 1310 && f.parsed?.longueurOnde !== 1550
    ).length;

    if (totalFiles === 2 && has1310 && has1550) {
      return {
        isValid: true,
        type: "ok",
        message: "1310nm + 1550nm",
        details: { nm1310: 1, nm1550: 1 },
      };
    }

    // Cas d'erreur : analyser le problème
    let errorType = "missing";
    let message = "";

    if (totalFiles > 2) {
      errorType = "duplicate";
      const details = [];
      if (count1310 > 1) details.push(`1310: ${count1310}x`);
      if (count1550 > 1) details.push(`1550: ${count1550}x`);
      if (count1310 === 1) details.push(`1310: 1x`);
      if (count1550 === 1) details.push(`1550: 1x`);
      if (countOther > 0) details.push(`autre: ${countOther}x`);

      message = `${totalFiles} fichiers (${details.join(", ")})`;
    } else {
      const missing = [];
      if (!has1310) missing.push("1310nm");
      if (!has1550) missing.push("1550nm");
      message = `Manque: ${missing.join(", ")}`;
    }

    return {
      isValid: false,
      type: errorType,
      message: message,
      details: {
        nm1310: count1310,
        nm1550: count1550,
        other: countOther,
        total: totalFiles,
      },
    };
  }

  /**
   * Valider les doublons d'une fibre
   */
  static _validateFiberDuplicates(fiber) {
    const filesByWavelength = {};
    const duplicates = [];

    fiber.files.forEach((file) => {
      const wavelength = file.parsed?.longueurOnde;
      if (!filesByWavelength[wavelength]) {
        filesByWavelength[wavelength] = [];
      }
      filesByWavelength[wavelength].push(file);
    });

    // Détecter les doublons
    Object.keys(filesByWavelength).forEach((wavelength) => {
      const files = filesByWavelength[wavelength];
      if (files.length > 1) {
        duplicates.push({
          wavelength: wavelength,
          count: files.length,
          files: files.map((f) => f.name),
        });
      }
    });

    if (duplicates.length === 0) {
      return {
        isValid: true,
        type: "ok",
        message: "Pas de doublons",
      };
    }

    const duplicateMessages = duplicates.map((d) => `${d.wavelength}nm: ${d.count} fichiers`);

    return {
      isValid: false,
      type: "duplicate",
      message: `Doublons détectés: ${duplicateMessages.join(", ")}`,
      duplicates: duplicates,
    };
  }
}
